export interface MessageDraft {
  text: string
  files: File[]
}

export interface TicketWithMessagesPayload {
  messages: { message: string | null }[]
  fileMessageMapping: number[]
  files: File[]
}

/**
 * Maps composed message drafts to the POST /tickets/with-messages payload shape.
 * `fileMessageMapping` is 0-indexed and each index may appear at most once (BE constraint,
 * CLIENT.TICKET.INVALID.FILE.MESSAGE.MAPPING) — a draft with multiple files is expanded into
 * multiple message slots (first carries the text, the rest are null) rather than reusing one
 * index per file.
 */
export function buildTicketWithMessagesPayload(allMessages: MessageDraft[]): TicketWithMessagesPayload {
  const messages: { message: string | null }[] = []
  const fileMessageMapping: number[] = []
  const files: File[] = []

  for (const msg of allMessages) {
    if (msg.files.length === 0) {
      messages.push({ message: msg.text.trim() ? msg.text : null })
      continue
    }
    msg.files.forEach((file, i) => {
      messages.push({ message: i === 0 && msg.text.trim() ? msg.text : null })
      fileMessageMapping.push(messages.length - 1)
      files.push(file)
    })
  }

  return { messages, fileMessageMapping, files }
}

/**
 * Same slot-counting rule as buildTicketWithMessagesPayload (a draft with N files costs N slots,
 * a text-only draft costs 1), without allocating the messages/fileMessageMapping/files arrays —
 * cheap enough to call on every render (e.g. for the "(X/10)" counter and the add/attach guards).
 */
export function countMessageSlots(allMessages: MessageDraft[]): number {
  return allMessages.reduce((count, msg) => count + (msg.files.length > 0 ? msg.files.length : 1), 0)
}
